<template>
  <!-- Super admin user list: every user on the platform, where they work and when they last signed in (US 9436) -->
  <div class="adm-page">
    <section class="home-hero adm-hero">
      <div class="home-hero__main">
        <div class="home-hero__eyebrow">{{ $t('admin_page.eyebrow') }}</div>
        <h1 class="home-hero__title" qid="users-page-title">{{ $t('administration.users.title') }}</h1>
        <p class="home-hero__summary" qid="users-summary">{{ loading ? '\u00a0' : summaryText }}</p>

        <div class="home-search us-search">
          <font-awesome-icon icon="search" class="home-search__icon"/>
          <input ref="search"
                 v-model="keyword"
                 class="home-search__input"
                 :placeholder="$t('users_page.search_placeholder')"
                 :aria-label="$t('users_page.search_placeholder')"
                 autocomplete="off"
                 qid="users-search-input"
                 @input="debounceInput"
          />
          <span class="home-kbd">/</span>
        </div>

        <div class="home-hero__chips">
          <router-link to="/administration/users/create" class="home-chip home-chip--light" qid="administration-users-create-button">
            <font-awesome-icon icon="plus"/>{{ $t('admin_page.actions.new_user') }}
          </router-link>
        </div>
      </div>

      <div v-if="counts.all" class="home-week us-health" qid="users-health">
        <div class="home-week__top">
          <svg class="home-ring"
               width="96"
               height="96"
               viewBox="0 0 96 96"
               aria-hidden="true">
            <circle class="home-ring__track"
                    cx="48"
                    cy="48"
                    r="42"/>
            <circle v-if="counts.active"
                    class="home-ring__value"
                    cx="48"
                    cy="48"
                    r="42"
                    :stroke-dasharray="ringDash(counts.active, counts.all, 42)"
            />
          </svg>
          <b class="home-week__count us-health__count">{{ counts.active }}/{{ counts.all }}</b>
          <div>
            <div class="home-week__title">{{ $t('admin_page.users.health_title') }}</div>
            <div class="home-week__text">{{ $t('admin_page.users.health_text') }}</div>
          </div>
        </div>
        <button v-if="counts.never"
                type="button"
                class="us-health__line"
                @click="setActivity('never')">
          <span class="us-dot us-dot--amber"></span>{{ $tc('admin_page.users.health_never', counts.never, { count: counts.never }) }}
        </button>
      </div>
    </section>

    <div class="us-bar">
      <div class="us-seg" role="tablist">
        <button v-for="activity in activities"
                :key="activity"
                type="button"
                role="tab"
                :class="{ 'is-on': filters.activity === activity }"
                :aria-selected="filters.activity === activity ? 'true' : 'false'"
                :qid="'users-activity-' + activity"
                @click="setActivity(activity)"
        >
          {{ $t('admin_page.users.activity.' + activity) }}
          <em>{{ counts[activity] }}</em>
        </button>
      </div>
      <span class="us-grow"></span>
      <multiselect v-model="filters.authentication"
                   class="us-filter"
                   :options="signInOptions"
                   track-by="value"
                   label="text"
                   :placeholder="$t('users_page.filter.sign_in')"
                   select-label="✔"
                   selected-label="✔"
                   deselect-label="x"
                   qid="users-filter-sign-in"
                   @input="loadUsers()"
      ></multiselect>
    </div>

    <div class="v2-card us-card">
      <div class="us-table-wrap">
        <table class="us-table adm-table">
          <thead>
            <tr>
              <th v-for="column in columns"
                  :key="column.key"
                  :class="{ 'is-sortable': column.sort }"
                  :aria-sort="column.sort && selectedSort === column.key ? (selectedDirection === 'asc' ? 'ascending' : 'descending') : null"
                  :qid="'users-sort-' + column.key"
                  @click="column.sort && selectSort(column.key)"
              >
                {{ $t('admin_page.users.table.' + column.key) }}
                <font-awesome-icon v-if="column.sort && selectedSort === column.key"
                                   :icon="selectedDirection === 'asc' ? 'caret-up' : 'caret-down'"
                                   class="ml-1"
                />
              </th>
              <th class="us-table__actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in tableItems" :key="user.id" qid="users-row">
              <td>
                <router-link :to="{ name: 'administration.userView', params: { slug: user.slug } }"
                             class="us-who adm-who"
                             qid="administration-users-name">
                  <span class="us-avatar" :style="{ backgroundImage: 'url(' + avatarUrl(user) + ')' }"></span>
                  <span class="adm-who__text">
                    <span class="us-who__name">{{ user.name }}</span>
                    <span class="adm-who__meta">{{ user.email }}</span>
                  </span>
                </router-link>
              </td>
              <td>
                <span class="us-method">
                  <SignInMethodIcon :method="user.authentication || 'Application'"/>
                  {{ $t('users_page.sign_in.' + (user.authentication || 'Application')) }}
                </span>
                <div v-if="user.authentication === 'Microsoft' && user.upn_email && user.upn_email !== user.email"
                     class="adm-who__meta">{{ user.upn_email }}</div>
              </td>
              <td>
                <span class="adm-chips">
                  <router-link v-for="workspace in (user.workspaces || []).slice(0, 2)"
                               :key="workspace.id"
                               :to="{ name: 'administration.clientView', params: { slug: workspace.slug } }"
                               class="adm-chip"
                               v-b-tooltip.hover
                               :title="workspace.parent_name ? $t('admin_page.overview.partition_of', { client: workspace.parent_name }) : null"
                  >{{ workspace.name }}</router-link>
                  <button v-if="(user.workspaces || []).length > 2"
                          type="button"
                          class="adm-chip adm-chip--more"
                          @click="$refs.edit.open(user)"
                  >+{{ user.workspaces.length - 2 }}</button>
                  <span v-if="!(user.workspaces || []).length" class="us-muted">–</span>
                </span>
              </td>
              <td>
                <div v-if="user.last_login_at"
                     class="us-last"
                     v-b-tooltip.hover
                     :title="formatDateTime(user.last_login_at)">
                  <span class="us-last__date">{{ formatDateTime(user.last_login_at) }}</span>
                  <span class="us-last__ago">{{ user.last_login_at | formatRelativeDate }}</span>
                </div>
                <span v-else class="v2-tag v2-tag--amber">{{ $t('admin_page.users.activity.never') }}</span>
              </td>
              <td class="us-muted">{{ formatDate(user.created_at) }}</td>
              <td class="us-table__actions">
                <button type="button"
                        class="us-icon-btn"
                        v-b-tooltip.hover
                        :title="$t('users_page.actions.edit')"
                        :aria-label="$t('users_page.actions.edit')"
                        qid="administration-users-edit-button"
                        @click="$refs.edit.open(user)"
                >
                  <font-awesome-icon icon="pen"/>
                </button>
                <b-dropdown v-if="!isMe(user)"
                            right
                            no-caret
                            variant="link"
                            toggle-class="us-icon-btn"
                            qid="administration-users-more-button"
                >
                  <template #button-content>
                    <font-awesome-icon icon="ellipsis-h"/>
                    <span class="sr-only">{{ $t('users_page.actions.more') }}</span>
                  </template>
                  <b-dropdown-item :to="{ name: 'administration.userView', params: { slug: user.slug } }">
                    <font-awesome-icon icon="external-link-alt" class="mr-2"/>{{ $t('admin_page.users.open_profile') }}
                  </b-dropdown-item>
                  <b-dropdown-item variant="danger" qid="administration-users-delete-button" @click="showConfirmDeleteModal(user)">
                    <font-awesome-icon icon="trash-alt" class="mr-2"/>{{ $t('admin_page.users.delete') }}
                  </b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
            <tr v-if="!loading && !tableItems.length" qid="no-results-row">
              <td :colspan="columns.length + 1" class="us-empty">{{ $t('system.no_results') }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="us-foot">
        <span class="us-foot__count">{{ pageText }}</span>
        <multiselect v-model="perPage"
                     class="us-filter us-filter--small"
                     :options="perPageOptions"
                     track-by="value"
                     label="value"
                     :allow-empty="false"
                     :placeholder="selectedPerPage"
                     :select-label="$t('system.dropdown_select_label')"
                     :selected-label="$t('system.dropdown_selected_label')"
                     :deselect-label="$t('system.dropdown_deselect_label')"
                     qid="users-per-page"
                     @select="perPageChanged"
        ></multiselect>
        <span class="us-grow"></span>
        <b-pagination v-model="currentPage"
                      class="us-pagination"
                      :total-rows="totalRows"
                      :per-page="Number(selectedPerPage)"
                      @change="loadUsers"
                      limit="5"
        ></b-pagination>
      </div>
    </div>

    <AdminUserEditPanel ref="edit" @saved="loadUsers(currentPage)"></AdminUserEditPanel>

    <!-- Delete user from the platform -->
    <b-modal ref="delete-user" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <h5 class="mb-0" qid="delete-user-title">{{ $t('administration.users.delete.title') }}</h5>
          <button type="button"
                  class="us-icon-btn"
                  :aria-label="$t('administration.users.delete.cancel')"
                  @click="hideConfirmDeleteModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <div class="us-remove">
        <span class="us-avatar" :style="{ backgroundImage: 'url(' + avatarUrl(itemToDelete) + ')' }"></span>
        <div>
          <b>{{ itemToDelete.name }}</b>
          <div class="us-muted">{{ itemToDelete.email }}</div>
        </div>
      </div>
      <div class="us-note us-note--warn mt-3">
        <font-awesome-icon icon="exclamation-triangle"/>
        <span>{{ $tc('admin_page.users.delete_text', (itemToDelete.workspaces || []).length, { count: (itemToDelete.workspaces || []).length }) }}</span>
      </div>
      <div class="us-modal-foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="delete-user-cancel-button" @click="hideConfirmDeleteModal">
          {{ $t('administration.users.delete.cancel') }}
        </b-button>
        <b-button variant="danger"
                  :disabled="deleting"
                  qid="delete-user-submit-button"
                  @click="confirmDeleteUser(itemToDelete)">
          <font-awesome-icon v-if="deleting" icon="spinner" class="fa-spin mr-1"/>
          {{ $t('admin_page.users.delete') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import {config} from '@/config.js';
import {userAvatarUrl} from '@/util/userAvatar.js';
import SignInMethodIcon from '@/components/user/SignInMethodIcon.vue';
import AdminUserEditPanel from '@/components/administration/AdminUserEditPanel.vue';

const ACTIVITIES = ['all', 'active', 'inactive', 'never'];

export default {
  name: 'AdministrationUsers',
  components: {
    SignInMethodIcon,
    AdminUserEditPanel
  },
  data() {
    let query = this.$route.query;
    return {
      tableItems: [],
      counts: { all: 0, active: 0, inactive: 0, never: 0 },
      activities: ACTIVITIES,
      columns: [
        { key: 'name', sort: true },
        { key: 'sign_in' },
        { key: 'workspaces' },
        { key: 'last_login_at', sort: true },
        { key: 'created_at', sort: true }
      ],
      keyword: '',
      filters: {
        activity: ACTIVITIES.indexOf(query.activity) !== -1 ? query.activity : 'all',
        authentication: null
      },
      selectedSort: query.activity === 'active' ? 'last_login_at' : 'name',
      selectedDirection: query.activity === 'active' ? 'desc' : 'asc',
      perPageOptions: [
        { value: '10' },
        { value: '20' },
        { value: '50' },
        { value: '100' }
      ],
      perPage: null,
      selectedPerPage: '20',
      currentPage: 1,
      totalRows: 0,
      itemToDelete: {},
      loading: true,
      deleting: false
    }
  },
  computed: {
    summaryText() {
      let sentences = [this.$tc('admin_page.users.summary', this.counts.all, { count: this.counts.all })];
      if (this.counts.active) {
        sentences.push(this.$tc('admin_page.users.summary_active', this.counts.active, { count: this.counts.active }));
      }
      return sentences.join(' ');
    },
    signInOptions() {
      return ['Microsoft', 'Google', 'Application'].map(value => ({ value: value, text: this.$t('users_page.sign_in.' + value) }));
    },
    pageText() {
      let from = (this.currentPage - 1) * Number(this.selectedPerPage) + 1;
      let to = Math.min(from + Number(this.selectedPerPage) - 1, this.totalRows);
      return this.totalRows ? this.$t('users_page.pagination', { from: from, to: to, total: this.totalRows }) : '';
    }
  },
  methods: {
    debounceInput: _.debounce(function() {
      this.loadUsers();
    }, 400),
    loadUsers(page) {
      let self = this;
      this.currentPage = page || 1;
      piincHttp.get('users', { params: {
        page: this.currentPage,
        per_page: this.selectedPerPage,
        with_clients: 1,
        keyword: this.keyword || undefined,
        activity: this.filters.activity !== 'all' ? this.filters.activity : undefined,
        authentication: this.filters.authentication ? this.filters.authentication.value : undefined,
        sort: this.selectedSort,
        order: this.selectedDirection
      }}).then(function(response) {
        self.tableItems = response.data.items;
        self.totalRows = response.data.total_count;
        self.counts = response.data.activity_counts || self.counts;
        self.loading = false;
      }, function() {
        self.loading = false;
      });
    },
    setActivity(activity) {
      this.filters.activity = activity;
      this.loadUsers();
    },
    selectSort(sort) {
      // Same column flips the direction; names start A–Z, dates newest first
      if (sort === this.selectedSort) {
        this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
      } else {
        this.selectedSort = sort;
        this.selectedDirection = sort === 'name' ? 'asc' : 'desc';
      }
      this.loadUsers();
    },
    perPageChanged(perPage) {
      this.selectedPerPage = perPage.value;
      this.loadUsers();
    },
    // The 120px photo when the list sent it, so avatars stay sharp
    avatarUrl(user) {
      return userAvatarUrl(user, true);
    },
    isMe(user) {
      return this.$store.state.user && user.id === this.$store.state.user.id;
    },
    ringDash(done, total, radius) {
      let circumference = 2 * Math.PI * radius;
      let length = total ? circumference * Math.min(done / total, 1) : 0;
      return length + ' ' + circumference;
    },
    formatDate(date) {
      return date ? this.$options.filters.formatDate(date, config.dateFormat) : '';
    },
    formatDateTime(date) {
      return this.$options.filters.formatDateTime(date, config.dateFormat + ' HH:mm');
    },
    showConfirmDeleteModal(user) {
      this.itemToDelete = user;
      this.$refs['delete-user'].show();
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {};
      this.$refs['delete-user'].hide();
    },
    confirmDeleteUser(user) {
      let self = this;
      this.deleting = true;
      piincHttp.delete('users/' + user.slug).then(function() {
        self.deleting = false;
        self.hideConfirmDeleteModal();
        self.loadUsers(self.currentPage);
        self.$toastr('success', self.$t('administration.users.delete.success'));
      }, function() {
        self.deleting = false;
      });
    },
    // "/" focuses the search, unless you are typing somewhere
    onKeydown(event) {
      let tag = event.target.tagName;
      if (event.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA' && !event.target.isContentEditable) {
        event.preventDefault();
        this.$refs.search.focus();
      }
    }
  },
  created() {
    this.loadUsers();
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  }
}
</script>
